// A collection scheduled for automatic deletion is shown in the danger style once fewer than this many seconds
// remain (7 days; 86400 is the number of seconds in a day).
const DELETION_WARNING_SECONDS = 86400 * 7;

/**
 * True when the deletion date is still ahead but less than a week away. A date already in the past is not
 * imminent: it means the policy has not run yet, not that deletion is about to happen.
 */
export const isDeletionImminent = (autoDeleteAt: string | Date, now: Date = new Date()): boolean => {
    const secondsToDelete = (new Date(autoDeleteAt).getTime() - now.getTime()) / 1000;
    return secondsToDelete >= 0 && secondsToDelete < DELETION_WARNING_SECONDS;
};

export type DeletionDisplay = {
    /** `deleted`: the collection has been deleted (show only that fact). `scheduled`: it will be deleted on `date`. */
    kind: 'deleted' | 'scheduled';
    date: string;
    imminent: boolean;
};

/**
 * What to show about a collection's retention policy, or undefined when there is no deletion date. A collection
 * that has been deleted is only ever shown as deleted; it is never also shown as scheduled for deletion.
 */
export const getDeletionDisplay = (
    summary: { autoDeleteAt?: string; isDeleted?: boolean } | undefined,
    now: Date = new Date(),
): DeletionDisplay | undefined => {
    if (!summary || !summary.autoDeleteAt) {
        return undefined;
    }
    if (summary.isDeleted) {
        return { kind: 'deleted', date: summary.autoDeleteAt, imminent: false };
    }
    return { kind: 'scheduled', date: summary.autoDeleteAt, imminent: isDeletionImminent(summary.autoDeleteAt, now) };
};
